/* eslint-disable max-lines-per-function */
import { useHookstate } from '@hookstate/core'
import { useHistory } from 'react-router-dom'
import { useLang } from '~/hooks/useLang'
import notify from '~/utils/notify'
import type {
  DisabledNextButton,
  UserListItem,
} from '../interfaces/IWarehouseForm'
import { createWarehouse } from '../services'
import IAgency from '~/features/Agency/interfaces/IAgency'
import { useEffect, useState } from 'react'
import { useAuth } from '~/features/auth/hooks/useAuth'

const disabledNextButtonDefault: DisabledNextButton = {
  disabledBasicInfo: true,
  disabledManagers: false,
  disabledOperators: false,
}

export function useWarehouseForm() {
  const { translate } = useLang()
  const history = useHistory()
  const { user } = useAuth()

  const name = useHookstate<string>('')
  const agency = useHookstate<IAgency | null>(null)
  const [managersList, setManagersList] = useState<readonly UserListItem[]>([])
  const [operatorsList, setOperatorsList] = useState<readonly UserListItem[]>(
    []
  )
  const activeStep = useHookstate<number>(0)
  const disabledNextButton = useHookstate<DisabledNextButton>(
    disabledNextButtonDefault
  )
  const isSubmitting = useHookstate<boolean>(false)

  // Adicionar usuário logado como gerente automaticamente
  useEffect(() => {
    if (user && managersList.length === 0) {
      setManagersList([
        {
          id: user.id as number,
          name: user.name || user.representation_name || '',
          representation_name: user.representation_name || '',
          registration: user.registration || undefined,
          email: user.email || undefined,
        },
      ])
    }
  }, [user])

  const handleCancelButton = () => {
    history.push('/warehouse')
  }

  const handleNext = () => {
    if (activeStep.get() < 3) {
      activeStep.set((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (activeStep.get() > 0) {
      activeStep.set((prev) => prev - 1)
    }
  }

  const handleReset = () => {
    name.set('')
    agency.set(null)
    setManagersList([])
    setOperatorsList([])
    activeStep.set(0)
    disabledNextButton.set(disabledNextButtonDefault)
  }

  const addManager = (user: any): boolean => {
    const userAlreadyExists = managersList.some(
      (manager) => manager.id === user.id
    )

    if (userAlreadyExists) {
      notify({
        message: translate('User already added to managers list'),
        type: 'warning',
      })
      return false
    }

    const operatorExists = operatorsList.some(
      (operator) => operator.id === user.id
    )
    if (operatorExists) {
      notify({
        message: translate('User is already in operators list'),
        type: 'warning',
      })
      return false
    }

    setManagersList([
      ...managersList,
      {
        id: user.id,
        name: user.name || user.representation_name || '',
        representation_name: user.representation_name || '',
        registration: user.registration || undefined,
        email: user.email || undefined,
      },
    ])

    return true
  }

  const removeManager = (userId: number) => {
    const updatedList = managersList.filter((manager) => manager.id !== userId)
    setManagersList(updatedList)
  }

  const addOperator = (user: any): boolean => {
    const userAlreadyExists = operatorsList.some(
      (operator) => operator.id === user.id
    )

    if (userAlreadyExists) {
      notify({
        message: translate('User already added to operators list'),
        type: 'warning',
      })
      return false
    }

    const managerExists = managersList.some((manager) => manager.id === user.id)
    if (managerExists) {
      notify({
        message: translate('User is already in managers list'),
        type: 'warning',
      })
      return false
    }

    setOperatorsList([
      ...operatorsList,
      {
        id: user.id,
        name: user.name || user.representation_name || '',
        registration: user.registration || undefined,
        representation_name: user.representation_name || '',
        email: user.email || undefined,
      },
    ])

    return true
  }

  const removeOperator = (userId: number) => {
    const updatedList = operatorsList.filter(
      (operator) => operator.id !== userId
    )
    setOperatorsList(updatedList)
  }

  const handleCreateWarehouse = async () => {
    try {
      isSubmitting.set(true)

      const agencyData = agency.get()
      if (!agencyData?.id) {
        notify({
          message: translate('Agency is required'),
          type: 'error',
        })
        return
      }
      const users = [
        ...managersList
          .filter(
            (manager) =>
              manager.id !== null &&
              manager.id !== undefined &&
              !isNaN(Number(manager.id))
          ) // Filtrar apenas managers com id válido
          .map((manager) => ({
            user_id: manager.id,
            is_manager: true,
            is_operator: false,
          })),
        ...operatorsList
          .filter(
            (operator) =>
              operator.id !== null &&
              operator.id !== undefined &&
              !isNaN(Number(operator.id))
          ) // Filtrar apenas operators com id válido
          .map((operator) => ({
            user_id: operator.id,
            is_manager: false,
            is_operator: true,
          })),
      ]

      const payload = {
        name: name.get(),
        agency_id: agencyData.id,
        users: users,
      }

      await createWarehouse(payload)

      notify({
        message: translate('Warehouse successfully created!'),
        type: 'success',
      })
      history.push('/warehouse')
    } catch (error: any) {
      notify({
        message: error?.message || translate('Error creating warehouse'),
        type: 'error',
      })
    } finally {
      isSubmitting.set(false)
    }
  }

  // Funções de validação
  const getBasicInfoValidity = () => {
    return Boolean(name.get().trim()) && Boolean(agency.get()?.id)
  }

  const getManagersValidity = () => {
    return managersList.length >= 1
  }


  return {
    name,
    agency,
    managersList,
    operatorsList,
    activeStep,
    disabledNextButton,
    isSubmitting,
    // functions
    handleCancelButton,
    handleNext,
    handleBack,
    handleReset,
    addManager,
    removeManager,
    addOperator,
    removeOperator,
    handleCreateWarehouse,
    getBasicInfoValidity,
    getManagersValidity,
  }
}
